import React from 'react';
import { useLocation } from 'react-router';
import * as API from 'ff/debrief/features/TracklogApi';
import SegmentAnalytics from 'ff/common/SegmentAnalytics';
import { getCurrentPage } from '../utils';

interface EventProperties {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

function getCurrentEnvironment(): string {
    const hostname = window.location.hostname;

    if (hostname === 'beta.foreflight.com') {
        return 'beta';
    } else if (hostname === 'plan.foreflight.com') {
        return 'production';
    }

    // Default to 'development' for local development or unknown hosts
    return 'development';
}

export function useAnalytics() {
    const location = useLocation();
    const { data } = API.useGetDebriefSettingQuery();

    const debriefMode = data?.savedDebriefSettings.isScoreModeEnabled
        ? 'on'
        : 'off';

    const logEvent = React.useCallback(
        (eventName: string, properties?: EventProperties) => {
            const currentPage = getCurrentPage(location.pathname);
            const environment = getCurrentEnvironment();

            const enhancedProperties = {
                ...properties,
                currentPage,
                environment,
                debriefMode,
            };

            SegmentAnalytics.logEvent(eventName, enhancedProperties);
        },
        [location.pathname, debriefMode]
    );

    const logDebriefButtonClick = React.useCallback(
        (properties: { score: number; iapCount: number }) => {
            logEvent('Debrief, Debrief Button Clicked', {
                score: properties.score,
                iapCount: properties.iapCount,
            });
        },
        [logEvent]
    );

    const logAxisDataSelected = React.useCallback(
        (properties: {
            axis: 'left' | 'right';
            originalValue: string;
            newValue: string;
        }) => {
            logEvent('Debrief, Profile Axis Data Selected', {
                axis: properties.axis,
                originalValue: properties.originalValue,
                newValue: properties.newValue,
            });
        },
        [logEvent]
    );

    const logSegmentClicked = React.useCallback(
        (properties: { segmentName: string; segmentScore: number | null }) => {
            logEvent('Debrief, Segment Clicked', {
                segmentName: properties.segmentName,
                segmentScore: properties.segmentScore,
            });
        },
        [logEvent]
    );

    const logIAPChanged = React.useCallback(
        (properties: {
            previousApproach: string;
            newApproach: string;
            isVisualApproach: boolean;
        }) => {
            logEvent('Debrief, IAP Changed', {
                previousApproach: properties.previousApproach,
                newApproach: properties.newApproach,
                isVisualApproach: properties.isVisualApproach,
            });
        },
        [logEvent]
    );

    const logTrackLogDownloaded = React.useCallback(
        (properties: { fileType: string }) => {
            logEvent('Debrief, Track Log Downloaded', {
                fileType: properties.fileType,
            });
        },
        [logEvent]
    );

    const logShareButtonClicked = React.useCallback(() => {
        logEvent('Debrief, Share Button Clicked');
    }, [logEvent]);

    const logInfoDialogOpened = React.useCallback(
        (properties: { score: number | null }) => {
            logEvent('Debrief, Info Dialog Opened', {
                score: properties.score,
            });
        },
        [logEvent]
    );

    const logMakePrivateClicked = React.useCallback(() => {
        logEvent('Debrief, Make Private Clicked');
    }, [logEvent]);

    const logTrackLogDeleted = React.useCallback(() => {
        logEvent('Debrief, Track Log Deleted');
    }, [logEvent]);

    const logTrackLogSelected = React.useCallback(
        (properties: { score: number | null; shared: boolean }) => {
            logEvent('Debrief, Track Log Selected', {
                score: properties.score,
                shared: properties.shared,
            });
        },
        [logEvent]
    );

    const logAircraftConfigChanged = React.useCallback(
        (properties: {
            tailChanged: 'yes' | 'no';
            aircraftChanged: 'yes' | 'no';
            vspeedChanged: 'yes' | 'no';
        }) => {
            logEvent('Debrief, Aircraft Dialog Saved', {
                changedTailNumber: properties.tailChanged,
                changedAircraftType: properties.aircraftChanged,
                changedVspeed: properties.vspeedChanged,
            });
        },
        [logEvent]
    );

    const logDeclutterButtonClicked = React.useCallback(
        (properties: { state: 'open' | 'closed' }) => {
            logEvent('Debrief, Declutter Button Clicked', {
                state: properties.state,
            });
        },
        [logEvent]
    );

    const logVRefOverridden = React.useCallback(
        (properties: {
            approachType: string;
            originalValue: number;
            overrideValue: number;
        }) => {
            logEvent('Debrief, VRef Overridden', {
                approachType: properties.approachType,
                originalValue: properties.originalValue,
                overrideValue: properties.overrideValue,
            });
        },
        [logEvent]
    );

    const logTimelineSpeedChanged = React.useCallback(
        (properties: { speed: number; whilePlaying: boolean }) => {
            logEvent('Debrief, Timeline Speed Changed', {
                speed: properties.speed,
                whilePlaying: properties.whilePlaying,
            });
        },
        [logEvent]
    );

    const logMapLayerChanged = React.useCallback(
        (properties: { layerName: string }) => {
            logEvent('Debrief, Map Layer Changed', {
                layerName: properties.layerName,
            });
        },
        [logEvent]
    );

    const logInstrumentsToggled = React.useCallback(
        (properties: { state: 'open' | 'closed' }) => {
            logEvent('Debrief, Instruments Toggled', {
                state: properties.state,
            });
        },
        [logEvent]
    );

    const logDataPaneToggled = React.useCallback(
        (properties: { state: 'open' | 'closed' }) => {
            logEvent('Debrief, Data Pane Toggled', {
                state: properties.state,
            });
        },
        [logEvent]
    );

    const logScoreDetailsCollapseToggled = React.useCallback(
        (properties: { state: 'open' | 'closed' }) => {
            logEvent('Debrief, Score Details Collapse Toggled', {
                state: properties.state,
            });
        },
        [logEvent]
    );

    const logWhatsNewItemViewed = React.useCallback(
        (properties: {
            itemName: string;
            trigger: 'dropdown' | 'next_button';
            wasUnread: boolean;
        }) => {
            logEvent('Debrief, Whats New Item Viewed', {
                itemName: properties.itemName,
                trigger: properties.trigger,
                wasUnread: properties.wasUnread,
            });
        },
        [logEvent]
    );

    const logNonScoredModeToggled = React.useCallback(
        (properties: { toggledState: 'On' | 'Off' }) => {
            logEvent('Debrief, Scoring Mode Toggled', {
                toggledState: properties.toggledState,
            });
        },
        [logEvent]
    );

    const logScoreDetailsRowExpanded = React.useCallback(
        (properties: { graderName: string; graderScore: number }) => {
            logEvent('Debrief, Score Detail Row Expanded', {
                GraderName: properties.graderName,
                GraderScore: properties.graderScore,
            });
        },
        [logEvent]
    );

    return {
        logEvent: logEvent,
        logDebriefButtonClick: logDebriefButtonClick,
        logAxisDataSelected: logAxisDataSelected,
        logSegmentClicked: logSegmentClicked,
        logIAPChanged: logIAPChanged,
        logTrackLogDownloaded: logTrackLogDownloaded,
        logShareButtonClicked: logShareButtonClicked,
        logInfoDialogOpened: logInfoDialogOpened,
        logMakePrivateClicked: logMakePrivateClicked,
        logTrackLogDeleted: logTrackLogDeleted,
        logTrackLogSelected: logTrackLogSelected,
        logAircraftConfigChanged: logAircraftConfigChanged,
        logDeclutterButtonClicked: logDeclutterButtonClicked,
        logVRefOverridden: logVRefOverridden,
        logTimelineSpeedChanged: logTimelineSpeedChanged,
        logMapLayerChanged: logMapLayerChanged,
        logInstrumentsToggled: logInstrumentsToggled,
        logDataPaneToggled: logDataPaneToggled,
        logScoreDetailsCollapseToggled: logScoreDetailsCollapseToggled,
        logWhatsNewItemViewed: logWhatsNewItemViewed,
        logNonScoredModeToggled: logNonScoredModeToggled,
        logScoreDetailsRowExpanded: logScoreDetailsRowExpanded,
    };
}
